import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api.js';
import {
  getAgencyLicenses, getProducerLicenses,
  getAgencyLicense, getProducerLicense,
} from '@/services/licenses.service.js';

// Caller identity (admin? + resolved broker). Drives the producer scope
// toggle visibility and the producer-create broker default.
export function useLicenseMe() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    let aborted = false;
    api.get(`/api/admin/licenses/_me?_t=${Date.now()}`)
      .then((d) => { if (!aborted) setMe(d); })
      .catch(() => { if (!aborted) setMe({ is_admin: false }); });
    return () => { aborted = true; };
  }, []);
  return me;
}

// List hook for either kind. `scope` only applies to producer ('all' = admin).
export function useLicenseList(kind, { scope } = {}) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = kind === 'agency'
        ? await getAgencyLicenses()
        : await getProducerLicenses({ scope });
      if (!aborted.current) { setRows(Array.isArray(d) ? d : []); setError(null); }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [kind, scope]);

  useEffect(() => {
    aborted.current = false;
    refresh();
    return () => { aborted.current = true; };
  }, [refresh]);

  return { rows, error, loading, refresh };
}

// Single-license hook for detail pages.
export function useLicense(kind, id) {
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const aborted = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = kind === 'agency' ? await getAgencyLicense(id) : await getProducerLicense(id);
      if (!aborted.current) { setRow(d); setError(null); }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [kind, id]);

  useEffect(() => {
    aborted.current = false;
    refresh();
    return () => { aborted.current = true; };
  }, [refresh]);

  return { row, error, loading, refresh };
}
